
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChevronRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useIsMobile } from '@/hooks/use-mobile';

const SectionThree = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data: images } = useQuery({
    queryKey: ["images", "section-three"],
    queryFn: async () => {
      console.log("Fetching images for section-three");
      const { data, error } = await supabase
        .from("images")
        .select("*")
        .eq("section", "section-three")
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const getBackgroundImage = () => {
    if (images && images.length > 0) {
      const { data } = supabase.storage
        .from('images')
        .getPublicUrl(images[0].storage_path);
      return data.publicUrl;
    }
    return 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80';
  };

  const scrollToNextSteps = () => {
    const section = document.getElementById('next-steps');
    if (section) {
      const offset = -80;
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const handleVisionClick = () => {
    const section = document.getElementById('ns5');
    if (section) {
      const offset = -80;
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div id="ns3" className="relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/50" />
      <div className="relative bg-gradient-to-r from-[#243949] to-[#517fa4] [&>*]:relative">
        <div className="container mx-auto px-4 py-8 md:py-16">
          <h3 className="text-lg md:text-xl font-bold text-white mb-8 text-center uppercase">
            Die beste Coach-Wahl für dich
          </h3>
          
          {isMobile ? (
            <div className="flex flex-col md:flex-row md:items-center md:space-x-8">
              <div className="w-full md:w-1/2 mb-8 md:mb-0 flex justify-center">
                <div className="relative aspect-[16/9] w-full max-w-xl">
                  <img
                    src={getBackgroundImage()}
                    alt="Beratung und Coaching"
                    className="rounded-lg object-cover w-full h-full border border-[#9F9EA1]"
                  />
                </div>
              </div>
              <div className="w-full md:w-1/2 space-y-4">
                <div 
                  className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/20"
                  onClick={() => navigate("/pfarrer")}
                >
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                      <ChevronRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-white pl-4 text-left">
                    Ich war Ev. Pfarrer und bin jetzt Unternehmensberater
                  </p>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                    <ChevronRight className="w-4 h-4 text-white" />
                    <ChevronRight className="w-4 h-4 text-white -ml-2" />
                  </div>
                </div>
                
                <div 
                  className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/20"
                  onClick={() => navigate("/begleitung")}
                >
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                      <ChevronRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-white pl-4 text-left">
                    Als Pfarrer habe ich Menschen einfühlsam auf ihrem Lebensweg begleitet
                  </p>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                    <ChevronRight className="w-4 h-4 text-white" />
                    <ChevronRight className="w-4 h-4 text-white -ml-2" />
                  </div>
                </div>
                
                <div 
                  className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/20"
                  onClick={() => navigate("/strategie")}
                >
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                      <ChevronRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-white pl-4 text-left">
                    Als Strategieberater analysiere ich strukturiert Situationen, damit Klienten faktenbasiert richtige Entscheidungen treffen
                  </p>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                    <ChevronRight className="w-4 h-4 text-white" />
                    <ChevronRight className="w-4 h-4 text-white -ml-2" />
                  </div>
                </div>
                
                <div 
                  className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/20"
                  onClick={handleVisionClick}
                >
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                      <ChevronRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-white pl-4 text-left">
                    Es ist meine Vision, dass (junge) Menschen beruflich ihr Potential entfalten und glücklich sind
                  </p>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                    <ChevronRight className="w-4 h-4 text-white" />
                    <ChevronRight className="w-4 h-4 text-white -ml-2" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {/* Header image with max-w-xl to match NS5 size */}
              <div className="w-full max-w-xl mb-8">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg shadow-lg border border-white/20">
                  <div className="relative aspect-[16/9] w-full">
                    <img
                      src={getBackgroundImage()}
                      alt="Beratung und Coaching"
                      className="rounded-lg object-cover w-full h-full border-2 border-white/40"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                <div 
                  className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/20"
                  onClick={() => navigate("/pfarrer")}
                >
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                      <ChevronRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-white pl-4 text-left">
                    Ich war Ev. Pfarrer und bin jetzt Unternehmensberater
                  </p>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                    <ChevronRight className="w-4 h-4 text-white" />
                    <ChevronRight className="w-4 h-4 text-white -ml-2" />
                  </div>
                </div>
                
                <div 
                  className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/20"
                  onClick={() => navigate("/begleitung")}
                >
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                      <ChevronRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-white pl-4 text-left">
                    Als Pfarrer habe ich Menschen einfühlsam auf ihrem Lebensweg begleitet
                  </p>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                    <ChevronRight className="w-4 h-4 text-white" />
                    <ChevronRight className="w-4 h-4 text-white -ml-2" />
                  </div>
                </div>
                
                <div 
                  className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/20"
                  onClick={() => navigate("/strategie")}
                >
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                      <ChevronRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-white pl-4 text-left">
                    Als Strategieberater analysiere ich strukturiert Situationen, damit Klienten faktenbasiert richtige Entscheidungen treffen
                  </p>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                    <ChevronRight className="w-4 h-4 text-white" />
                    <ChevronRight className="w-4 h-4 text-white -ml-2" />
                  </div>
                </div>
                
                <div 
                  className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/20"
                  onClick={handleVisionClick}
                >
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                      <ChevronRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-white pl-4 text-left">
                    Es ist meine Vision, dass (junge) Menschen beruflich ihr Potential entfalten und glücklich sind
                  </p>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                    <ChevronRight className="w-4 h-4 text-white" />
                    <ChevronRight className="w-4 h-4 text-white -ml-2" />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-8">
            <div 
              className="relative bg-gradient-to-r from-[#D3E4FD] to-[#F1F0FB] py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6 cursor-pointer transition-all hover:bg-white/10"
              onClick={() => navigate("/vision")}
            >
              <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-base md:text-lg text-[#222222] pl-4 text-left">
                Die Erfahrung meiner zwei Berufe und meine Persönlichkeit bieten eine einzigartige Kombi für deine Berufsfindung
              </p>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center animate-horizontal-bounce">
                <ChevronRight className="w-4 h-4 text-[#222222]" />
                <ChevronRight className="w-4 h-4 text-[#222222] -ml-2" />
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <Button 
              onClick={scrollToNextSteps}
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-[#ECA505] px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all cursor-pointer"
            >
              Interesse am Coaching mit mir? <div className="flex items-center">
                <ChevronRight className="w-4 h-4" />
                <ChevronRight className="w-4 h-4 -ml-2" />
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionThree;
